<script setup lang="ts">
import { type ChangelogEntry } from '@/modules/HelpCenter/useChangelogStore'
import { GO_CHANGELOGS_URL } from '@/shared/utils'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import { format as formatDate } from 'date-fns'

defineProps<{
  changelogs: ChangelogEntry[]
}>()

const emit = defineEmits<{
  (e: 'click', changelogUrl: string): void
}>()

const { captureAnalyticsEvent } = useAnalytics()

const onChangelogClicked = (changelog: ChangelogEntry) => {
  captureAnalyticsEvent(ANALYTICS_EVENT.CHANGELOG_OPENED, { changelog: changelog.title })

  if (!changelog.url) {
    return
  }

  emit('click', changelog.url)

  window.open(changelog.url, '_blank', 'noopener')
}

const openExternalChangelogs = () => {
  captureAnalyticsEvent(ANALYTICS_EVENT.ALL_CHANGELOG_OPENED)

  window.open(GO_CHANGELOGS_URL, '_blank')
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <h2 class="px-3 text-lg-15px-bold text-text">Changelog</h2>
    <div
      v-if="changelogs.length > 0"
      class="flex flex-col"
    >
      <!-- Changelog entries -->
      <div
        v-for="(changelog, index) in changelogs"
        :key="changelog.url"
        class="group flex cursor-pointer gap-3 rounded-corner-8 bg-background-transparent px-3 hover:bg-background-transparent-hovered active:bg-background-transparent-pressed"
        @click="onChangelogClicked(changelog)"
      >
        <!-- Timeline element -->
        <div
          v-if="index === 0"
          class="flex flex-col gap-2 pb-2 pt-4"
        >
          <div
            v-for="n in Array.from({ length: 7 }).keys()"
            :key="n"
            class="w-1.5 outline outline-[0.5px] outline-icon-disabled"
            :class="{
              '!outline-icon': n === 0,
              'w-3': n === 0,
            }"
          />
        </div>
        <div
          v-else
          class="flex flex-col gap-2"
        >
          <div
            v-for="n in Array.from({ length: 10 }).keys()"
            :key="n"
            class="w-1.5 outline outline-[0.5px] outline-icon-disabled"
            :class="{
              '!outline-icon': n === 2,
              'w-3': n === 2,
            }"
          />
        </div>

        <div class="flex flex-col gap-1 py-2">
          <div class="flex items-center gap-1 text-xs-11px-light text-text-subtlest">
            <div
              v-if="changelog.new"
              class="relative size-1.5 rounded-full bg-icon-warning"
            />
            <span>{{ formatDate(changelog.date, 'MMM d') }}</span>
            <span>·</span>
            <span>{{ changelog.type }}</span>
          </div>

          <!-- Title -->
          <h3 class="line-clamp-3 text-sm-12px-light text-text">
            {{ changelog.title }}
          </h3>
        </div>
      </div>
      <div
        class="flex cursor-pointer gap-2 rounded-corner-8 bg-background-transparent px-3 hover:bg-background-transparent-hovered active:bg-background-transparent-pressed"
      >
        <!-- Timeline element -->
        <div class="flex flex-col gap-2">
          <div
            v-for="n in Array.from({ length: 3 }).keys()"
            :key="n"
            class="w-1.5 outline outline-[0.5px] outline-icon-disabled"
            :class="{
              '!outline-icon': n === 2,
              'w-3': n === 2,
            }"
          />
        </div>

        <div class="flex flex-col gap-0.5">
          <DarwinButton
            class="w-full !p-0.5"
            size="md"
            variant="transparent-link"
            rounded
            @click="openExternalChangelogs"
          >
            Open full changelog
          </DarwinButton>
        </div>
      </div>
    </div>
  </div>
</template>
